import React, { useState } from 'react';
import { useTranslation } from 'react-i18next'; // Remove useI18next import
import { Container } from '../styles/styled';
import styled from 'styled-components';
import Logo from '../images/logo.svg';
import { Menu, X, Globe } from 'lucide-react';



const NavWrapper = styled.nav`
  position: sticky;
  top: 10px;
  z-index: 1000;

  > div {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
    padding: 10px 20px;
    border-radius: 20px;
    margin-top: 40px;
    background-color: #fff;

    @media (max-width: 768px) {
      padding: 10px 15px;
      margin-top: 20px;
    }

    > nav {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }
`;

const LogoLink = styled.a`
  display: flex;
  align-items: center;

  img {
    height: 45px;
    @media (max-width: 768px) {
      height: 35px;
    }
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  column-gap: 18px;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    right: ${props => (props.$isOpen ? '0' : '-100%')};
    width: 280px;
    height: 100vh;
    background-color: #fff;
    flex-direction: column;
    align-items: flex-start;
    padding: 80px 30px 30px;
    box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
    transition: right 0.3s ease-in-out;
    row-gap: 20px;
    z-index: 999;
  }

  > a {
    color: #000;
    text-decoration: none;
    font-size: 14px;
    white-space: nowrap;

    @media (max-width: 1024px) {
      font-size: 15px;
    }

    @media (max-width: 768px) {
      font-size: 18px;
      width: 100%;
      padding: 10px 0;
      border-bottom: 1px solid #f0f0f0;
    }

    &:hover {
      color: #002dff;
    }
  }
`;

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: 15px;

  @media (max-width: 768px) {
    column-gap: 10px;
  }
`;

const LanguageSelector = styled.div`
  position: relative;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileLanguageSelector = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    gap: 10px;
    width: 100%;
    padding: 15px 0;
    border-top: 1px solid #f0f0f0;
    margin-top: 10px;
  }
`;

const MobileLanguageButton = styled.button`
  flex: 1;
  padding: 10px;
  border: 1px solid #002dff;
  border-radius: 8px;
  background: ${props => props.$isActive ? '#002dff' : 'transparent'};
  color: ${props => props.$isActive ? '#fff' : '#002dff'};
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background-color: ${props => props.$isActive ? '#002dff' : '#f0f5ff'};
  }
`;

const LanguageButton = styled.button`
  border: 1px solid #002dff;
  padding: 8px 12px;
  border-radius: 8px;
  font-weight: 600;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  color: #002dff;
  font-size: 14px;

  &:hover {
    background-color: #f0f5ff;
  }
`;

const LanguageDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  min-width: 120px;
  display: ${props => (props.$isOpen ? 'block' : 'none')};
`;

const LanguageOption = styled.button`
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: ${props => (props.$isActive ? '#f0f5ff' : 'transparent')};
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  color: ${props => (props.$isActive ? '#002dff' : '#000')};
  font-weight: ${props => (props.$isActive ? '600' : '400')};

  &:hover {
    background-color: #f0f5ff;
  }
`;

const GetStartedButton = styled.a`
  padding: 10px 20px;
  border-radius: 10px;
  color: #002dff;
  font-weight: 700;
  background-color: #78ffd6;
  text-decoration: none;
  white-space: nowrap;
  display: inline-block;

  @media (max-width: 768px) {
    font-size: 16px;
    width: 100%;
    text-align: center;
    margin-top: 20px;
  }

  &:hover {
    background-color: #29f0b4;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 5px;
  color: #002dff;

  @media (max-width: 768px) {
    display: block;
  }
`;

const CloseButton = styled.button`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: absolute;
    top: 20px;
    right: 20px;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 5px;
    color: #002dff;
  }
`;

const Overlay = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: ${props => (props.$isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 998;
  }
`;

const Navbar = () => {
  const { t, i18n } = useTranslation('common'); // Get i18n from useTranslation
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);
  const toggleLanguageDropdown = () => setLanguageDropdownOpen(!languageDropdownOpen);
  
  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang); // Use i18n from hook
    setLanguageDropdownOpen(false);
  };

  const currentLang = i18n.language.toUpperCase(); // Use i18n from hook

  return (
    <>
      <Overlay $isOpen={mobileMenuOpen} onClick={toggleMobileMenu} />
      <NavWrapper>
        <Container>
          <nav>
            <LogoLink href="/">
              <img src={Logo} alt="logo" />
            </LogoLink>

            <NavLinks $isOpen={mobileMenuOpen}>
  <CloseButton onClick={toggleMobileMenu}>
    <X size={24} />
  </CloseButton>
  <a href="#what-we-do" onClick={() => setMobileMenuOpen(false)}>{t('whatWeDo')}</a>
  <a href="#why-hurrlink" onClick={() => setMobileMenuOpen(false)}>{t('whyHurrlink')}</a>
  <a href="#for-banks" onClick={() => setMobileMenuOpen(false)}>{t('forBanks')}</a>
  <a href="#contact" onClick={() => setMobileMenuOpen(false)}>{t('contact')}</a>

  <MobileLanguageSelector>
    <MobileLanguageButton
      $isActive={i18n.language === 'en'}
      onClick={() => changeLanguage('en')}
    >
      English
    </MobileLanguageButton>
    <MobileLanguageButton
      $isActive={i18n.language === 'ru'}
      onClick={() => changeLanguage('ru')}
    >
      Русский
    </MobileLanguageButton>
  </MobileLanguageSelector>

  {/* <GetStartedButton href="#contact" onClick={() => setMobileMenuOpen(false)}>
    {t('getStarted')}
  </GetStartedButton> */}
</NavLinks>


            <ActionsWrapper>
              <LanguageSelector>
                <LanguageButton onClick={toggleLanguageDropdown}>
                  <Globe size={16} />
                  {currentLang}
                </LanguageButton>
                <LanguageDropdown $isOpen={languageDropdownOpen}>
                  <LanguageOption
                    $isActive={i18n.language === 'en'}
                    onClick={() => changeLanguage('en')}
                  >
                    English
                  </LanguageOption>
                  <LanguageOption
                    $isActive={i18n.language === 'ru'}
                    onClick={() => changeLanguage('ru')}
                  >
                    Русский
                  </LanguageOption>
                </LanguageDropdown>
              </LanguageSelector>

              <MobileMenuButton onClick={toggleMobileMenu}>
                <Menu size={24} />
              </MobileMenuButton>
            </ActionsWrapper>
          </nav>
        </Container>
      </NavWrapper>
    </>
  );
};

export default Navbar;